import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Heart, Sparkles, Eye, Hand, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { t, languages } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Landing() {
  const { lang, setLang } = useLang();
  const { user } = useAuth();

  const steps = [
    { icon: Eye, key: "observation" },
    { icon: Heart, key: "feeling" },
    { icon: Sparkles, key: "need" },
    { icon: Hand, key: "request" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Leaf className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <span className="font-heading text-lg font-semibold">{t(lang, "app_name")}</span>
        </div>
        <div className="flex items-center gap-3">
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="landing-lang" className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map((l) => (
                <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user ? (
            <Link to="/app"><Button data-testid="go-app-btn">{t(lang, "dashboard")}</Button></Link>
          ) : (
            <>
              <Link to="/login"><Button data-testid="header-login-btn" variant="ghost">{t(lang, "login")}</Button></Link>
              <Link to="/register"><Button data-testid="header-register-btn">{t(lang, "register")}</Button></Link>
            </>
          )}
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-24 grid md:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-xs uppercase tracking-widest text-accent font-medium mb-4">
            {t(lang, "tagline")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-tight">
            {t(lang, "hero_title")}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
            {t(lang, "hero_sub")}
          </p>
          <div className="mt-8 flex gap-3">
            <Link to={user ? "/app" : "/register"}>
              <Button data-testid="cta-primary-btn" size="lg" className="rounded-full px-7 gap-2">
                {t(lang, "get_started")} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="cta-secondary-btn" size="lg" variant="outline" className="rounded-full px-7">
                {t(lang, "login")}
              </Button>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative"
        >
          <div className="absolute -inset-6 bg-secondary rounded-3xl -z-10" />
          <img
            src="https://images.pexels.com/photos/7624975/pexels-photo-7624975.jpeg"
            alt="Calm conversation"
            className="rounded-2xl w-full object-cover aspect-[4/5]"
          />
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="font-heading text-2xl sm:text-3xl font-medium mb-10">{t(lang, "the_four_steps")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="p-6 rounded-xl border border-border bg-card card-lift"
            >
              <div className="w-10 h-10 rounded-lg bg-secondary text-primary flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-heading text-xl font-medium">{t(lang, s.key)}</div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
